import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewContainerRef,
  ComponentRef,
  input,
  inject,
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  OverlayPositionBuilder,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from '../components/tooltip/tooltip.component';

@Directive({
  selector: '[appTooltip]',
})
export class TooltipDirective implements OnDestroy {
  tooltipText = input<string>('', { alias: 'appTooltip' });
  tooltipTitle = input<string | null>('');
  tooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');

  tooltipDisabled = input<boolean>(false);

  private _overlayRef: OverlayRef | null = null;

  private _elementRef = inject(ElementRef);
  private _overlay = inject(Overlay);
  private overlayPositionBuilder = inject(OverlayPositionBuilder);
  private _viewContainerRef = inject(ViewContainerRef);

  private _positions: Record<string, ConnectedPosition[]> = {
    top: [
      {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetY: -8,
      },
    ],
    bottom: [
      {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetY: 8,
      },
    ],
    left: [
      {
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center',
        offsetX: -8,
      },
    ],
    right: [
      {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
        offsetX: 14,
      },
    ],
  };

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.tooltipDisabled() || !this.tooltipText().trim()) {
      return;
    }
    this._showTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this._hideTooltip();
  }

  private _showTooltip(): void {
    if (this._overlayRef?.hasAttached()) {
      return;
    }

    if (!this._overlayRef) {
      const positionStrategy = this.overlayPositionBuilder
        .flexibleConnectedTo(this._elementRef)
        .withPositions(this._getPositions())
        .withPush(false);

      this._overlayRef = this._overlay.create({
        positionStrategy,
        scrollStrategy: this._overlay.scrollStrategies.close(),
      });
    }

    const tooltipPortal = new ComponentPortal(
      TooltipComponent,
      this._viewContainerRef,
    );
    const tooltipRef: ComponentRef<TooltipComponent> =
      this._overlayRef.attach(tooltipPortal);
    tooltipRef.instance.text = this.tooltipText();
    tooltipRef.instance.title = this.tooltipTitle();
    tooltipRef.instance.position = this.tooltipPosition();
  }

  private _hideTooltip(): void {
    if (this._overlayRef?.hasAttached()) {
      this._overlayRef.detach();
    }
  }

  private _getPositions(): ConnectedPosition[] {
    return this._positions[this.tooltipPosition()] || this._positions['top'];
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }
}
